import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { DivTooltip, ProgressBar, Tooltip, TooltipBody, TooltipHeader } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { t } from '~/helpers/localization';

import styles from './TaskRow.scss';

import type { ReactNode } from 'react';
import type { ClanTaskReward } from '~/Actions/ActionClanTasks';

export const renderCheckedSpan = (children: ReactNode) => {
  return (
    <span className={styles.active}>
      <span className={styles.checkIcon} />
      &nbsp;&nbsp;{children}
    </span>
  );
};

interface TaskRowBaseProps {
  taskId?: number;
  clanId?: number;
  name?: string;
  order?: number;
  claimedAt?: string;
  finishedTill?: string;
  progress?: number;
  isFinished: boolean;
  isClaimed: boolean;
  reward?: ClanTaskReward;

  title?: string;
  description?: string;
  binary?: boolean;
  total?: number;
  taskIconTooltip?: React.ReactNode;
  animated?: boolean;

  children?: React.ReactNode;
}

const TaskRowBase = forwardRef<HTMLDivElement, TaskRowBaseProps>(
  (
    {
      progress = 0,
      isFinished,
      isClaimed,

      title = '',
      description = '',
      binary = true,
      total = 0,
      taskIconTooltip = '',
      animated = false,

      children,
    },
    ref,
  ) => {
    const descriptionTooltip = description ? (
      <Tooltip>
        <TooltipHeader>
          <strong>{title}</strong>
        </TooltipHeader>
        <TooltipBody>{description}</TooltipBody>
      </Tooltip>
    ) : (
      <TooltipBody>
        <strong>{title}</strong>
      </TooltipBody>
    );
    const withoutProgress = isClaimed || binary;

    const progressValue = Math.floor(progress * total);

    return (
      <div
        className={classNames(styles.wrapper, {
          [styles.animated]: animated,
        })}
        ref={ref}
      >
        <div className={styles.background} />
        <div className={styles.focus} />
        <div className={styles.content}>
          <DivTooltip tooltipBody={<TooltipBody>{taskIconTooltip}</TooltipBody>}>
            <div className={styles.iconWrapper}>
              <div
                className={classNames(styles.taskIcon, {
                  [styles.finishedTaskIcon]: isFinished,
                  [styles.claimedTaskIcon]: isClaimed,
                })}
              />
            </div>
          </DivTooltip>
          <div
            className={classNames(styles.descriptionWrapper, {
              [styles.descriptionWrapperBig]: withoutProgress,
            })}
          >
            <div className={styles.titleWrapper}>
              <DivTooltip tooltipBody={descriptionTooltip} className={styles.divTooltipTitle}>
                <span
                  className={classNames(styles.title, {
                    [styles.active]: isFinished,
                  })}
                >
                  {title}
                  <span className={styles.infoIcon} />
                </span>
              </DivTooltip>
            </div>
            {!withoutProgress ? (
              <div className={styles.progress}>
                <DivTooltip
                  tooltipBody={
                    <Tooltip>
                      <TooltipHeader>
                        <strong>{t('Прогресс выполнения задач')}</strong>
                      </TooltipHeader>
                      <TooltipBody>
                        {t(
                          'Информация о прогрессе выполнения задач обновляется  ежедневно в {update_time} по серверному времени',
                        ).replace('{update_time}', settings.darwin.syncTime)}
                      </TooltipBody>
                    </Tooltip>
                  }
                >
                  <ProgressBar completed={progressValue / total} size="small" color="#ffcc66" />
                  <span className={styles.progressCaption}>
                    {progressValue}
                    <span>/{total}</span>
                  </span>
                </DivTooltip>
              </div>
            ) : null}
          </div>
          <div className={styles.rewardWrapper}>{children}</div>
        </div>
      </div>
    );
  },
);

TaskRowBase.displayName = 'TaskRowBase';

export default TaskRowBase;
